/**
 * Google Calendar Service
 * Fetches and parses public Google Calendar iCal feeds
 */

// List of CORS proxies to try (in order of preference)
// Each proxy has a different URL format requirement
const CORS_PROXIES = [
  { url: 'https://corsproxy.io/?', encode: true },
  { url: 'https://api.allorigins.win/raw?url=', encode: true },
  { url: 'https://thingproxy.freeboard.io/fetch/', encode: false },
  { url: 'https://api.codetabs.com/v1/proxy?quest=', encode: true },
];

/**
 * Get the Google Calendar iCal URL from calendar ID
 * @param {string} calendarId - Google Calendar ID
 * @returns {string} - iCal feed URL
 */
export const getCalendarIcalUrl = (calendarId) => {
  // Encode only the @ symbol for the URL path (not full URI encoding)
  // Google Calendar expects the email with @ encoded as %40 in the path
  const encodedId = calendarId.replace('@', '%40');
  return `https://calendar.google.com/calendar/ical/${encodedId}/public/basic.ics`;
};

/**
 * Parse an iCal date string to JavaScript Date
 * Handles both DATE and DATETIME formats
 * @param {string} dateStr - iCal date string (YYYYMMDD or YYYYMMDDTHHMMSSZ)
 * @returns {Date} - JavaScript Date object
 */
const parseICalDate = (dateStr) => {
  if (!dateStr) return null;
  
  // Clean the string - remove VALUE=DATE: prefix but keep the date value
  let cleanStr = dateStr;
  if (cleanStr.startsWith('VALUE=DATE:')) {
    cleanStr = cleanStr.replace('VALUE=DATE:', '');
  }
  
  // Full datetime format: YYYYMMDDTHHMMSSZ or YYYYMMDDTHHMMSS
  if (cleanStr.includes('T')) {
    const year = parseInt(cleanStr.substring(0, 4));
    const month = parseInt(cleanStr.substring(4, 6)) - 1;
    const day = parseInt(cleanStr.substring(6, 8));
    const hour = parseInt(cleanStr.substring(9, 11)) || 0;
    const minute = parseInt(cleanStr.substring(11, 13)) || 0;
    const second = parseInt(cleanStr.substring(13, 15)) || 0;
    
    // If ends with Z, it's UTC - convert to local
    if (cleanStr.endsWith('Z')) {
      return new Date(Date.UTC(year, month, day, hour, minute, second));
    }
    
    // No Z suffix means it's already in local timezone (e.g., from TZID=Asia/Kolkata)
    // Create as local time
    return new Date(year, month, day, hour, minute, second);
  }
  
  // Date only format: YYYYMMDD
  const year = parseInt(cleanStr.substring(0, 4));
  const month = parseInt(cleanStr.substring(4, 6)) - 1;
  const day = parseInt(cleanStr.substring(6, 8));
  return new Date(year, month, day);
};

/**
 * Parse iCal content and extract events
 * @param {string} icalContent - Raw iCal file content
 * @returns {Array} - Array of event objects
 */
const parseICalContent = (icalContent) => {
  const events = [];
  
  // Split by VEVENT blocks
  const eventBlocks = icalContent.split('BEGIN:VEVENT');
  
  for (let i = 1; i < eventBlocks.length; i++) {
    const block = eventBlocks[i].split('END:VEVENT')[0];
    const lines = block.split(/\r?\n/);
    
    const event = {};
    let currentKey = '';
    let currentValue = '';
    
    for (const line of lines) {
      // Handle line folding (lines starting with space or tab are continuations)
      if (line.startsWith(' ') || line.startsWith('\t')) {
        currentValue += line.substring(1);
        continue;
      }
      
      // Save previous key-value pair
      if (currentKey) {
        event[currentKey] = currentValue;
      }
      
      // Parse new key-value pair
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        const fullKey = line.substring(0, colonIndex);
        currentKey = fullKey.split(';')[0]; // Get base key without parameters
        currentValue = line.substring(colonIndex + 1);
        
        // For DTSTART/DTEND, preserve the full key with TZID info
        if ((currentKey === 'DTSTART' || currentKey === 'DTEND') && fullKey.includes('TZID')) {
          // Store the full key so we know it has timezone info
          event[`${currentKey}_RAW`] = fullKey;
        }
      }
    }
    
    // Save last key-value pair
    if (currentKey) {
      event[currentKey] = currentValue;
    }
    
    // Extract relevant fields
    if (event.SUMMARY) {
      events.push({
        title: event.SUMMARY,
        start: parseICalDate(event.DTSTART),
        end: parseICalDate(event.DTEND),
        description: event.DESCRIPTION || '',
        location: event.LOCATION || '',
        uid: event.UID || '',
      });
    }
  }
  
  return events;
};

/**
 * Fetch events from a public Google Calendar
 * Tries multiple CORS proxies for reliability
 * @param {string} calendarId - Google Calendar ID (e.g., 'your-email@gmail.com')
 * @returns {Promise<Array>} - Array of event objects
 */
export const fetchCalendarEvents = async (calendarId) => {
  if (!calendarId) {
    return [];
  }
  
  const icalUrl = getCalendarIcalUrl(calendarId);
  
  // Try each CORS proxy until one works
  let lastError = null;
  
  // Add cache-busting timestamp to prevent stale cached responses
  const cacheBuster = `_cb=${Date.now()}`;
  const icalUrlWithCacheBuster = icalUrl.includes('?') 
    ? `${icalUrl}&${cacheBuster}` 
    : `${icalUrl}?${cacheBuster}`;
  
  for (const proxy of CORS_PROXIES) {
    try {
      // Some proxies need URL encoding, others don't
      const targetUrl = proxy.encode ? encodeURIComponent(icalUrlWithCacheBuster) : icalUrlWithCacheBuster;
      const proxyUrl = `${proxy.url}${targetUrl}`;
      
      const response = await fetch(proxyUrl, {
        headers: {
          'Accept': 'text/calendar, text/plain, */*',
        },
        cache: 'no-store', // Prevent browser from caching the response
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const icalContent = await response.text();
      
      // Verify we got valid iCal content
      if (!icalContent.includes('BEGIN:VCALENDAR')) {
        throw new Error('Invalid iCal response');
      }
      
      const events = parseICalContent(icalContent);
      return events;
      
    } catch (error) {
      lastError = error;
      // Continue to next proxy
    }
  }
  
  // All proxies failed
  throw new Error(`Failed to fetch calendar. ${lastError?.message || 'Calendar may not be public.'}`);
};

/**
 * Filter events that contain "#show" in the title
 * @param {Array} events - Array of event objects
 * @returns {Array} - Filtered array of show events
 */
export const filterShowEvents = (events) => {
  return events.filter(event => 
    event.title && event.title.toLowerCase().includes('#show')
  );
};

/**
 * Get events for the next N days
 * @param {Array} events - Array of event objects
 * @param {number} days - Number of days to look ahead (default: 30)
 * @returns {Array} - Filtered array of upcoming events
 */
export const getUpcomingEvents = (events, days = 30) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  const futureDate = new Date(now);
  futureDate.setDate(futureDate.getDate() + days);
  
  return events.filter(event => {
    if (!event.start) return false;
    return event.start >= now && event.start <= futureDate;
  });
};

/**
 * Format date as YYYY-MM-DD in local timezone
 * @param {Date} date - Date object
 * @returns {string} - Date string in YYYY-MM-DD format (local time)
 */
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Group events by date (YYYY-MM-DD format) in local timezone
 * @param {Array} events - Array of event objects
 * @returns {Object} - Map of date string to array of events
 */
export const groupEventsByDate = (events) => {
  const grouped = {};
  
  for (const event of events) {
    if (!event.start) continue;
    
    // Use local date instead of UTC to correctly show dates in user's timezone
    const dateKey = formatLocalDate(event.start);
    
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(event);
  }
  
  return grouped;
};

/**
 * Calculate availability status for each day
 * @param {Object} groupedEvents - Map of date string to array of events
 * @returns {Object} - Map of date string to availability status
 */
export const calculateAvailability = (groupedEvents) => {
  const availability = {};
  
  for (const [dateKey, events] of Object.entries(groupedEvents)) {
    const count = events.length;
    
    if (count === 0) {
      availability[dateKey] = 'available';
    } else if (count === 1) {
      availability[dateKey] = 'partial';
    } else {
      availability[dateKey] = 'booked';
    }
  }
  
  return availability;
};

/**
 * Main function to fetch and process calendar availability
 * @param {string} calendarId - Google Calendar ID
 * @param {number} days - Number of days to look ahead (default: 30)
 * @returns {Promise<Object>} - Object with availability map and events
 */
export const getCalendarAvailability = async (calendarId, days = 30) => {
  // Fetch all events
  const allEvents = await fetchCalendarEvents(calendarId);
  
  // Get upcoming events (all events, no filtering)
  const upcomingEvents = getUpcomingEvents(allEvents, days);
  
  // Group by date
  const groupedEvents = groupEventsByDate(upcomingEvents);
  
  // Calculate availability
  const availability = calculateAvailability(groupedEvents);
  
  return {
    availability,
    events: upcomingEvents,
    groupedEvents,
  };
};
